import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  clearAdminMapsUsageLogTail,
  getAdminMapsUsage,
  getAdminMapsUsageLogTail,
  resetAdminMapsUsage,
} from "../../api/adminMapsUsageApi";

function cls(...xs) {
  return xs.filter(Boolean).join(" ");
}

function Card({ title, value, hint, tone = "slate" }) {
  const toneClass =
    tone === "rose"
      ? "border-rose-200 bg-rose-50 text-rose-900"
      : tone === "amber"
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : tone === "emerald"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : "border-slate-200 bg-white text-slate-900";

  return (
    <div className={cls("rounded-2xl border p-4", toneClass)}>
      <div className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-70">{title}</div>
      <div className="mt-2 text-3xl font-black tracking-tight">{value}</div>
      {hint ? <div className="mt-2 text-xs opacity-80">{hint}</div> : null}
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-extrabold text-slate-900">{value}</div>
    </div>
  );
}

function formatSourceLabel(source) {
  return String(source || "unscoped")
    .split(".")
    .map((part) => (part ? part.replaceAll("_", " ") : part))
    .join(" / ");
}

function SourceTable({ sourceCounters }) {
  const sources = Object.entries(sourceCounters || {});

  if (!sources.length) {
    return <div className="px-5 py-8 text-sm text-slate-500">No Maps calls captured yet for this run.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
          <tr>
            <th className="px-5 py-3">Source</th>
            <th className="px-5 py-3">Geocoding</th>
            <th className="px-5 py-3">Distance Matrix</th>
            <th className="px-5 py-3">Routes</th>
            <th className="px-5 py-3">Total</th>
          </tr>
        </thead>
        <tbody>
          {sources.map(([source, counters]) => {
            const geocoding = Number(counters?.["Geocoding API"] || 0);
            const distanceMatrix = Number(counters?.["Distance Matrix API"] || 0);
            const routes = Number(counters?.["Routes API"] || 0);
            const total = geocoding + distanceMatrix + routes;
            return (
              <tr key={source} className="border-t border-slate-200 align-top">
                <td className="px-5 py-4">
                  <div className="font-semibold text-slate-900">{formatSourceLabel(source)}</div>
                  <div className="mt-1 break-all text-xs text-slate-500">{source}</div>
                </td>
                <td className="px-5 py-4 font-semibold text-slate-700">{geocoding}</td>
                <td className="px-5 py-4 font-semibold text-slate-700">{distanceMatrix}</td>
                <td className="px-5 py-4 font-semibold text-slate-700">{routes}</td>
                <td className="px-5 py-4 font-extrabold text-slate-900">{total}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminMapsUsageLogs() {
  const [usage, setUsage] = useState(null);
  const [logTail, setLogTail] = useState(null);
  const [lineLimit, setLineLimit] = useState(250);
  const [busy, setBusy] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const skuTotals = usage?.skuTotals || {};
  const totalCalls = Number(usage?.totalCalls || 0);
  const geocodingCalls = Number(skuTotals["Geocoding API"] || 0);
  const distanceMatrixCalls = Number(skuTotals["Distance Matrix API"] || 0);
  const routesCalls = Number(skuTotals["Routes API"] || 0);

  const hottestSource = useMemo(() => {
    const entries = Object.entries(usage?.sourceCounters || {});
    if (!entries.length) return null;
    return entries
      .map(([source, counters]) => ({
        source,
        total: Object.values(counters || {}).reduce((sum, value) => sum + Number(value || 0), 0),
      }))
      .sort((a, b) => b.total - a.total)[0];
  }, [usage]);

  const loadAll = useCallback(async (nextLimit = lineLimit) => {
    setBusy(true);
    setError("");
    try {
      const [usageData, tailData] = await Promise.all([
        getAdminMapsUsage(),
        getAdminMapsUsageLogTail(nextLimit),
      ]);
      setUsage(usageData);
      setLogTail(tailData);
    } catch (err) {
      setError(err?.message || "Failed to load Maps usage logs.");
    } finally {
      setBusy(false);
    }
  }, [lineLimit]);

  useEffect(() => {
    loadAll(lineLimit);
  }, [lineLimit, loadAll]);

  async function handleRefresh() {
    setNotice("");
    await loadAll(lineLimit);
  }

  async function handleResetCounters() {
    setActionBusy(true);
    setError("");
    setNotice("");
    try {
      await resetAdminMapsUsage();
      setNotice("Maps usage counters reset.");
      await loadAll(lineLimit);
    } catch (err) {
      setError(err?.message || "Failed to reset Maps usage counters.");
    } finally {
      setActionBusy(false);
    }
  }

  async function handleClearLog() {
    setActionBusy(true);
    setError("");
    setNotice("");
    try {
      await clearAdminMapsUsageLogTail();
      setNotice("Maps usage log file cleared.");
      await loadAll(lineLimit);
    } catch (err) {
      setError(err?.message || "Failed to clear Maps usage log.");
    } finally {
      setActionBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="rounded-[28px] border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-rose-500">Logs</div>
              <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-900">Maps Usage Diagnostics</h2>
              <p className="mt-2 max-w-3xl text-sm text-slate-600">
                Inspect Google Maps usage by source flow, compare ride-now versus schedule-trip impact,
                and read the live diagnostic log without leaving admin.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={busy || actionBusy}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-extrabold text-slate-900 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Refresh
              </button>
              <button
                type="button"
                onClick={handleResetCounters}
                disabled={busy || actionBusy}
                className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-extrabold text-amber-900 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Reset Counters
              </button>
              <button
                type="button"
                onClick={handleClearLog}
                disabled={busy || actionBusy}
                className="rounded-xl border border-rose-300 bg-rose-50 px-4 py-2.5 text-sm font-extrabold text-rose-900 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Clear Log
              </button>
            </div>
          </div>

          {error ? (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
              {error}
            </div>
          ) : null}

          {notice ? (
            <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
              {notice}
            </div>
          ) : null}
        </div>

        <div className="grid gap-4 px-5 py-5 sm:grid-cols-2 xl:grid-cols-4">
          <Card title="Total Calls" value={totalCalls} hint="All tracked Maps calls since last reset." />
          <Card title="Geocoding" value={geocodingCalls} hint="Address lookup and reverse geocode usage." tone="emerald" />
          <Card title="Distance Matrix" value={distanceMatrixCalls} hint="Usually the biggest billing risk in ETA and schedule flows." tone="rose" />
          <Card
            title="Routes"
            value={routesCalls}
            hint={hottestSource ? `Most active source: ${formatSourceLabel(hottestSource.source)}` : "No source has logged calls yet."}
            tone="amber"
          />
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-[28px] border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-lg font-black text-slate-900">Source Breakdown</div>
                <div className="mt-1 text-sm text-slate-600">See which backend flow is generating the Maps calls.</div>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:flex">
                <StatPill label="Sources" value={Object.keys(usage?.sourceCounters || {}).length} />
                <StatPill label="Log Lines" value={logTail?.totalLines || 0} />
              </div>
            </div>
          </div>
          {busy ? <div className="px-5 py-8 text-sm text-slate-500">Loading Maps usage summary...</div> : <SourceTable sourceCounters={usage?.sourceCounters} />}
        </div>

        <div className="rounded-[28px] border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <div className="text-lg font-black text-slate-900">Run Checklist</div>
                <div className="text-sm text-slate-600">Use the same sequence each time so the data stays easy to compare.</div>
              </div>
              <ol className="space-y-2 text-sm text-slate-700">
                <li>1. Reset counters and clear log before starting a test flow.</li>
                <li>2. Run one ride-now flow or one schedule-trip flow end to end.</li>
                <li>3. Refresh this page and compare the top sources and log tail.</li>
                <li>4. Repeat for the second flow and compare Distance Matrix usage.</li>
              </ol>
            </div>
          </div>
          <div className="px-5 py-4 sm:px-6">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Active Log File</div>
              <div className="mt-2 break-all text-sm font-semibold text-slate-900">
                {logTail?.path || "logs/maps-usage.log"}
              </div>
              <div className="mt-2 text-xs text-slate-500">
                {logTail?.exists
                  ? "This file is being written asynchronously by the backend Maps diagnostics logger."
                  : "The log file has not been created yet. It will appear after the first tracked Maps call."}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-[28px] border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="text-lg font-black text-slate-900">Raw Log Tail</div>
              <div className="mt-1 text-sm text-slate-600">Read the latest Maps usage lines directly from the backend log file.</div>
            </div>
            <label className="inline-flex items-center gap-3 text-sm font-semibold text-slate-700">
              <span>Lines</span>
              <select
                value={lineLimit}
                onChange={(e) => setLineLimit(Number(e.target.value))}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none transition focus:border-slate-400"
              >
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </label>
          </div>
        </div>

        <div className="px-5 py-5 sm:px-6">
          {busy ? (
            <div className="text-sm text-slate-500">Loading Maps usage log...</div>
          ) : logTail?.lines?.length ? (
            <pre className="max-h-[680px] overflow-auto rounded-2xl bg-slate-950 p-4 text-[12px] leading-6 text-slate-100 shadow-inner">
              {logTail.lines.join("\n")}
            </pre>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm text-slate-500">
              No Maps usage log lines yet. Run a ride-now or schedule-trip flow, then refresh this page.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
